import { useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const normalizeId = (id) => {
  if (id === null || id === undefined || id === "") return null;
  const parsed = Number(id);
  return Number.isFinite(parsed) ? parsed : null;
};

// Aceita a assinatura nova ({ ticketId, ticketTabelaPrecoId }) e a antiga
// ({ ticket, tabelaPrecoId }). Mantido para que call-sites legados em outras
// branches/PRs não quebrem silenciosamente após o refactor.
const resolveArgs = (raw) => {
  const params = raw || {};
  let ticketId = params.ticketId;
  let ticketTabelaPrecoId = params.ticketTabelaPrecoId;

  if (ticketId == null && params.ticket && params.ticket.id != null) {
    ticketId = params.ticket.id;
  }
  if (ticketTabelaPrecoId === undefined) {
    if (params.ticket && params.ticket.tabelaPrecoId !== undefined) {
      ticketTabelaPrecoId = params.ticket.tabelaPrecoId;
    } else if (params.tabelaPrecoId !== undefined) {
      ticketTabelaPrecoId = params.tabelaPrecoId;
    }
  }

  return { ticketId, ticketTabelaPrecoId };
};

// Regra de negócio:
//   - ticket.tabelaPrecoId === null  → ERP ainda não definiu a tabela do ticket.
//     O usuário pode trocar livremente; a seleção é puramente local (não há
//     persistência no backend do Whaticket).
//   - ticket.tabelaPrecoId !== null  → ERP fixou a tabela oficial. A seleção
//     local é descartada e o selector fica travado refletindo o valor do ERP.
//
// Nada nesta camada chama PUT/PATCH. O ticket.tabelaPrecoId é alterado apenas
// pelo ERP (que entrega via socket "ticket"/"update" → setTicket no parent).
const usePrecoTabelas = (rawArgs) => {
  const { ticketId, ticketTabelaPrecoId } = resolveArgs(rawArgs);
  const { user } = useContext(AuthContext);
  const userId = user && user.id;

  const erpTabelaId = normalizeId(ticketTabelaPrecoId);
  const erpDefinida = erpTabelaId !== null;

  // Override local: usado APENAS enquanto o ERP não definiu tabela. Permanece
  // estável durante a sessão e sobrevive a re-renders. Reseta quando o ticket
  // muda.
  const [localOverride, setLocalOverride] = useState(null);
  const [tabelas, setTabelas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Token monotônico de requisições para descartar respostas atrasadas quando
  // o usuário troca de ticket rapidamente.
  const requestTokenRef = useRef(0);

  useEffect(() => {
    setLocalOverride(null);
  }, [ticketId]);

  useEffect(() => {
    if (!ticketId || !userId) {
      setTabelas([]);
      setError(null);
      return undefined;
    }

    const token = ++requestTokenRef.current;
    let cancelled = false;
    setLoading(true);
    setError(null);

    api
      .get(`/price-tables/${ticketId}`)
      .then(({ data }) => {
        if (cancelled || token !== requestTokenRef.current) return;
        setTabelas(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (cancelled || token !== requestTokenRef.current) return;
        console.error("[usePrecoTabelas] falha ao carregar tabelas:", err && err.message);
        setTabelas([]);
        setError(err);
      })
      .finally(() => {
        if (!cancelled && token === requestTokenRef.current) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [ticketId, userId]);

  // Valor efetivo: ERP > override local > nada.
  const tabelaSelecionada = erpDefinida ? erpTabelaId : localOverride;

  // Setter que NÃO persiste. Quando o ERP já definiu, ignora silenciosamente —
  // o selector também estará desabilitado, então no fluxo normal isso não é
  // alcançável; a checagem só protege contra uso programático.
  const setTabelaSelecionada = useCallback(async (id) => {
    if (erpDefinida) return false;
    setLocalOverride(normalizeId(id));
    return true;
  }, [erpDefinida]);

  return {
    tabelas,
    tabelaSelecionada,
    setTabelaSelecionada,
    erpDefinida,
    loading,
    error,
  };
};

export default usePrecoTabelas;
